import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { EmailService, EmailStats } from '@/services/email-service';
import { EmailWizard } from '@/components/admin/email/EmailWizard';
import { toast } from 'sonner';
import { Loader2, Mail, Send, TrendingUp, Users, Eye, MousePointer, Settings, Image, Copy, Shield, AlertTriangle, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const EmailNotificationsPage: React.FC = () => {
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [unsubscribes, setUnsubscribes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showWizard, setShowWizard] = useState(false);

  // Send email form state
  const [emailForm, setEmailForm] = useState({
    to: '',
    subject: '',
    content: '',
    type: 'system' as 'system' | 'marketing',
    from: '',
    replyTo: '',
    sendCopy: false
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Sender settings state
  const [senderSettings, setSenderSettings] = useState({
    fromName: '',
    fromEmail: '',
    replyTo: ''
  });
  const [savingSenderSettings, setSavingSenderSettings] = useState(false);

  // Branding settings state
  const [brandingSettings, setBrandingSettings] = useState({
    bimiLogoUrl: '',
    bimiSelector: 'default',
    emailDomain: ''
  });
  const [savingBrandingSettings, setSavingBrandingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sendingBrandingTest, setSendingBrandingTest] = useState(false);
  const [brandingTestEmail, setBrandingTestEmail] = useState('');

  useEffect(() => {
    loadData();
    loadSenderSettings();
    loadBrandingSettings();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, messagesData, unsubscribesData] = await Promise.all([
        EmailService.getEmailStats(),
        EmailService.getEmailMessages(100),
        EmailService.getUnsubscribes()
      ]);
      
      setStats(statsData);
      setMessages(messagesData);
      setUnsubscribes(unsubscribesData);
    } catch (error: any) {
      console.error('Failed to load email data:', error);
      toast.error('Failed to load email data');
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSendingTest(true);
      await EmailService.sendTestEmail(testEmail);
      toast.success('Test email sent successfully!');
      setTestEmail('');
      loadData(); // Reload data to show new message
    } catch (error: any) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email: ' + error.message);
    } finally {
      setSendingTest(false);
    }
  };

  const loadSenderSettings = async () => {
    try {
      const { data: settings } = await supabase
        .from('app_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['email_default_from_name', 'email_default_from_email', 'email_default_reply_to']);

      if (settings) {
        setSenderSettings({
          fromName: settings.find(s => s.setting_key === 'email_default_from_name')?.setting_value || '',
          fromEmail: settings.find(s => s.setting_key === 'email_default_from_email')?.setting_value || '',
          replyTo: settings.find(s => s.setting_key === 'email_default_reply_to')?.setting_value || ''
        });
      }
    } catch (error: any) {
      console.error('Failed to load sender settings:', error);
    }
  };

  const saveSenderSettings = async () => {
    try {
      setSavingSenderSettings(true);
      
      const updates = [
        { setting_key: 'email_default_from_name', setting_value: senderSettings.fromName },
        { setting_key: 'email_default_from_email', setting_value: senderSettings.fromEmail },
        { setting_key: 'email_default_reply_to', setting_value: senderSettings.replyTo }
      ];

      for (const update of updates) {
        await supabase
          .from('app_settings')
          .upsert(update, { onConflict: 'setting_key' });
      }

      toast.success('Sender settings saved successfully!');
    } catch (error: any) {
      console.error('Failed to save sender settings:', error);
      toast.error('Failed to save sender settings: ' + error.message);
    } finally {
      setSavingSenderSettings(false);
    }
  };

  const loadBrandingSettings = async () => {
    try {
      const { data: settings } = await supabase
        .from('app_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['email_bimi_logo_url', 'email_bimi_selector', 'email_domain']);

      if (settings) {
        setBrandingSettings({
          bimiLogoUrl: settings.find(s => s.setting_key === 'email_bimi_logo_url')?.setting_value || '',
          bimiSelector: settings.find(s => s.setting_key === 'email_bimi_selector')?.setting_value || 'default',
          emailDomain: settings.find(s => s.setting_key === 'email_domain')?.setting_value || ''
        });
      }
    } catch (error: any) {
      console.error('Failed to load branding settings:', error);
    }
  };

  const saveBrandingSettings = async () => {
    try {
      setSavingBrandingSettings(true);
      
      const updates = [
        { setting_key: 'email_bimi_logo_url', setting_value: brandingSettings.bimiLogoUrl },
        { setting_key: 'email_bimi_selector', setting_value: brandingSettings.bimiSelector },
        { setting_key: 'email_domain', setting_value: brandingSettings.emailDomain }
      ];

      for (const update of updates) {
        await supabase
          .from('app_settings')
          .upsert(update, { onConflict: 'setting_key' });
      }

      toast.success('Branding settings saved successfully!');
    } catch (error: any) {
      console.error('Failed to save branding settings:', error);
      toast.error('Failed to save branding settings: ' + error.message);
    } finally {
      setSavingBrandingSettings(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is SVG
    if (!file.type.includes('svg')) {
      toast.error('Please upload an SVG file for BIMI compatibility');
      return;
    }

    try {
      setUploadingLogo(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `bimi-logo-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('email-branding')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('email-branding')
        .getPublicUrl(filePath);

      setBrandingSettings(prev => ({
        ...prev,
        bimiLogoUrl: publicUrl
      }));

      toast.success('Logo uploaded successfully!');
    } catch (error: any) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo: ' + error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const generateBimiTxt = () => {
    if (!brandingSettings.bimiLogoUrl || !brandingSettings.emailDomain) {
      return 'Please set logo URL and domain first';
    }
    return `v=BIMI1; l=${brandingSettings.bimiLogoUrl};`;
  };

  const copyBimiTxt = () => {
    const bimiTxt = generateBimiTxt();
    navigator.clipboard.writeText(bimiTxt);
    toast.success('BIMI TXT record copied to clipboard!');
  };

  const sendBrandingTestEmail = async () => {
    if (!brandingTestEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSendingBrandingTest(true);
      await EmailService.sendTestEmail(brandingTestEmail, 'Branding Test - Check Email Avatar Display');
      toast.success('Branding test email sent successfully!');
      setBrandingTestEmail('');
      loadData(); // Reload data to show new message
    } catch (error: any) {
      console.error('Failed to send branding test email:', error);
      toast.error('Failed to send branding test email: ' + error.message);
    } finally {
      setSendingBrandingTest(false);
    }
  };

  const sendEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSendingEmail(true);
      
      // Prepare recipients list
      const recipients = emailForm.to.split(',').map(e => e.trim());
      
      // Add sender's email to recipients if "Send me a copy" is checked
      const finalRecipients = emailForm.sendCopy && emailForm.from 
        ? [...recipients, emailForm.from]
        : recipients;
      
      // Use the unified sendEmail method with custom from/reply-to
      await EmailService.sendEmail({
        to: finalRecipients,
        subject: emailForm.subject,
        html: emailForm.content,
        from: emailForm.from || undefined, // Let backend use defaults if empty
        reply_to: emailForm.replyTo || undefined, // Let backend use defaults if empty
        campaign_id: emailForm.type === 'marketing' ? `campaign_${Date.now()}` : undefined
      });

      toast.success('Email sent successfully!');
      setEmailForm({ 
        to: '', 
        subject: '', 
        content: '', 
        type: 'system',
        from: '',
        replyTo: '',
        sendCopy: false
      });
      loadData(); // Reload data
    } catch (error: any) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { variant: 'secondary', label: 'Pending' },
      'sent': { variant: 'default', label: 'Sent' },
      'delivered': { variant: 'success', label: 'Delivered' },
      'opened': { variant: 'info', label: 'Opened' },
      'clicked': { variant: 'info', label: 'Clicked' },
      'bounced': { variant: 'destructive', label: 'Bounced' },
      'spam': { variant: 'destructive', label: 'Spam' },
      'failed': { variant: 'destructive', label: 'Failed' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge variant={config.variant as any}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {showWizard ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Super Email Campaign</h1>
            <Button onClick={() => setShowWizard(false)} variant="outline">
              Back to Dashboard
            </Button>
          </div>
          <EmailWizard onClose={() => setShowWizard(false)} />
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Email Notifications</h1>
            <div className="flex gap-2">
              <Button onClick={() => setShowWizard(true)} className="bg-gradient-to-r from-primary to-primary/80">
                <Sparkles className="w-4 h-4 mr-2" />
                Super Email Campaign
              </Button>
              <Button onClick={loadData} variant="outline">
                Refresh Data
              </Button>
            </div>
          </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="flex items-center p-6">
              <Mail className="h-8 w-8 text-blue-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sent</p>
                <p className="text-2xl font-bold">{stats.total_sent}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <TrendingUp className="h-8 w-8 text-green-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
                <p className="text-2xl font-bold">{stats.delivery_rate.toFixed(1)}%</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <Eye className="h-8 w-8 text-purple-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Open Rate</p>
                <p className="text-2xl font-bold">{stats.open_rate.toFixed(1)}%</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <MousePointer className="h-8 w-8 text-orange-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Click Rate</p>
                <p className="text-2xl font-bold">{stats.click_rate.toFixed(1)}%</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="send" className="space-y-6">
        <TabsList>
          <TabsTrigger value="send">Send Email</TabsTrigger>
          <TabsTrigger value="messages">Email Messages</TabsTrigger>
          <TabsTrigger value="unsubscribes">Unsubscribes</TabsTrigger>
          <TabsTrigger value="test">Test Email</TabsTrigger>
          <TabsTrigger value="settings">Sender Settings</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="send">
          <Card>
            <CardHeader>
              <CardTitle>Send Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Warning Alert */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Deliverability Tips:</p>
                    <ul className="mt-1 text-amber-700 space-y-1">
                      <li>• Use <code className="bg-amber-100 px-1 rounded">info@mail.tradebot365.com</code> for best delivery</li>
                      <li>• Write longer, professional content (avoid short messages)</li>
                      <li>• Use descriptive subjects (avoid spam trigger words)</li>
                      <li>• Check your Spam folder initially and mark as "Not Spam"</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email-type">Email Type</Label>
                  <select 
                    id="email-type"
                    className="w-full p-2 border rounded-md"
                    value={emailForm.type}
                    onChange={(e) => setEmailForm({ ...emailForm, type: e.target.value as 'system' | 'marketing' })}
                  >
                    <option value="system">System Notification</option>
                    <option value="marketing">Marketing Email</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="email-to">To (comma separated)</Label>
                  <Input
                    id="email-to"
                    placeholder="user1@example.com, user2@example.com"
                    value={emailForm.to}
                    onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email-from">From Email</Label>
                  <Input
                    id="email-from"
                    placeholder="info@mail.tradebot365.com"
                    value={emailForm.from}
                    onChange={(e) => setEmailForm({ ...emailForm, from: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to use default from sender settings
                  </p>
                </div>
                <div>
                  <Label htmlFor="email-reply-to">Reply-To Email</Label>
                  <Input
                    id="email-reply-to"
                    placeholder="info@mail.tradebot365.com"
                    value={emailForm.replyTo}
                    onChange={(e) => setEmailForm({ ...emailForm, replyTo: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to use default reply-to address
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  placeholder="e.g., Important Update About Your Trading Account"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: Use descriptive, professional subjects. Avoid "Test", "Hello", etc.
                </p>
              </div>

              <div>
                <Label htmlFor="email-content">Content (HTML)</Label>
                <Textarea
                  id="email-content"
                  placeholder="Write your email content here..."
                  rows={10}
                  value={emailForm.content}
                  onChange={(e) => setEmailForm({ ...emailForm, content: e.target.value })}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEmailForm({ 
                      ...emailForm, 
                      subject: "System Maintenance Notification - TradeBot365",
                      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">System Maintenance Notice</h2>
  
  <p>Dear Valued User,</p>
  
  <p>We wanted to inform you about upcoming scheduled maintenance on our TradeBot365 platform.</p>
  
  <div style="background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin: 20px 0;">
    <strong>Maintenance Details:</strong><br>
    • Date: [Insert Date]<br>
    • Time: [Insert Time Range]<br>
    • Duration: Approximately [Duration]<br>
    • Impact: Platform will be temporarily unavailable
  </div>
  
  <p>During this maintenance window, you may experience:</p>
  <ul>
    <li>Temporary inability to access your dashboard</li>
    <li>Brief interruption in trading signals</li>
    <li>Limited customer support availability</li>
  </ul>
  
  <p>We apologize for any inconvenience this may cause and appreciate your patience as we work to improve our services.</p>
  
  <p>Best regards,<br>
  <strong>TradeBot365 Technical Team</strong><br>
  <a href="mailto:support@tradebot365.com">support@tradebot365.com</a></p>
  
  <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;">
  <p style="font-size: 12px; color: #666;">
    This is an automated system notification. Please do not reply to this email.
  </p>
</div>`
                    })}
                  >
                    Use System Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEmailForm({ 
                      ...emailForm, 
                      subject: "TradeBot365 Weekly Market Update & Performance Insights",
                      content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h1 style="color: #2c3e50; text-align: center; margin-bottom: 30px;">Weekly Market Update</h1>
  
  <p>Hello [Name],</p>
  
  <p>We hope you're having a successful week in the markets! Here's your personalized trading update from TradeBot365.</p>
  
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin: 0 0 15px 0;">This Week's Highlights</h3>
    <ul style="margin: 0; padding-left: 20px;">
      <li>Market volatility increased by 15%</li>
      <li>3 new trading opportunities identified</li>
      <li>Average signal accuracy: 78%</li>
    </ul>
  </div>
  
  <h3 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 5px;">Market Analysis</h3>
  <p>This week saw increased volatility across major currency pairs, presenting both opportunities and challenges for traders. Our AI systems have identified several emerging patterns that could benefit your trading strategy.</p>
  
  <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h4 style="color: #2c3e50; margin: 0 0 10px 0;">Key Recommendations:</h4>
    <ol>
      <li>Monitor EUR/USD for potential breakout patterns</li>
      <li>Consider reducing position sizes during high-impact news events</li>
      <li>Review your risk management settings in the dashboard</li>
    </ol>
  </div>
  
  <p style="text-align: center; margin: 30px 0;">
    <a href="https://tradebot365.com/dashboard" style="background-color: #3498db; color: white; padding: 12px 25px; text-decoration: none; border-radius: 5px; display: inline-block;">View Your Dashboard</a>
  </p>
  
  <p>As always, remember that trading involves risk, and past performance doesn't guarantee future results. Please trade responsibly and within your risk tolerance.</p>
  
  <p>Happy trading!<br>
  <strong>The TradeBot365 Team</strong></p>
  
  <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;">
  <p style="font-size: 12px; color: #666; text-align: center;">
    You're receiving this email because you're a valued TradeBot365 user.<br>
    <a href="#" style="color: #666;">Unsubscribe</a> | <a href="#" style="color: #666;">Update Preferences</a>
  </p>
</div>`
                    })}
                  >
                    Use Marketing Template
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Tip: Longer, professional content performs better. Plain text will be auto-generated.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="send-copy"
                  checked={emailForm.sendCopy}
                  onChange={(e) => setEmailForm({ ...emailForm, sendCopy: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="send-copy" className="text-sm">
                  Send me a copy for testing
                </Label>
              </div>

              <Button 
                onClick={sendEmail} 
                disabled={sendingEmail}
                className="w-full"
              >
                {sendingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Email
              </Button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Resend Webhook Configuration</p>
                    <p className="mt-1 text-blue-700">
                      To track email delivery status, configure this webhook URL in your Resend dashboard:
                    </p>
                    <code className="block mt-2 bg-blue-100 px-2 py-1 rounded text-xs break-all">
                      https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/resend-webhook
                    </code>
                    <p className="mt-2 text-blue-600">
                      <a href="https://resend.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline">
                        → Configure in Resend Dashboard
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle>Recent Email Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">{message.subject}</p>
                        <p className="text-sm text-gray-600">To: {message.to_email}</p>
                      </div>
                      {getStatusBadge(message.status)}
                    </div>
                    <div className="text-sm text-gray-500">
                      <p>From: {message.from_email}</p>
                      <p>Sent: {new Date(message.created_at).toLocaleString()}</p>
                      {message.delivered_at && <p>Delivered: {new Date(message.delivered_at).toLocaleString()}</p>}
                      {message.opened_at && <p>Opened: {new Date(message.opened_at).toLocaleString()}</p>}
                      {message.error_message && (
                        <p className="text-red-600">Error: {message.error_message}</p>
                      )}
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No email messages found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unsubscribes">
          <Card>
            <CardHeader>
              <CardTitle>Unsubscribed Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {unsubscribes.map((unsub, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{unsub.email}</p>
                        <p className="text-sm text-gray-600">Reason: {unsub.reason}</p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(unsub.unsubscribed_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                {unsubscribes.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No unsubscribes found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle>Send Test Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="test-email">Test Email Address</Label>
                <Input
                  id="test-email"
                  type="email"
                  placeholder="your-email@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              <Button 
                onClick={sendTestEmail} 
                disabled={sendingTest}
                className="w-full"
              >
                {sendingTest && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Send className="mr-2 h-4 w-4" />
                Send Test Email
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Sender Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="sender-name">Default Sender Name</Label>
                <Input
                  id="sender-name"
                  placeholder="Trade Bot 365"
                  value={senderSettings.fromName}
                  onChange={(e) => setSenderSettings({ ...senderSettings, fromName: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="sender-email">Default Sender Email</Label>
                <Input
                  id="sender-email"
                  type="email"
                  placeholder="info@mail.tradebot365.com"
                  value={senderSettings.fromEmail}
                  onChange={(e) => setSenderSettings({ ...senderSettings, fromEmail: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="reply-to">Default Reply-To Email</Label>
                <Input
                  id="reply-to"
                  type="email"
                  placeholder="info@mail.tradebot365.com"
                  value={senderSettings.replyTo}
                  onChange={(e) => setSenderSettings({ ...senderSettings, replyTo: e.target.value })}
                />
              </div>
              <Button 
                onClick={saveSenderSettings} 
                disabled={savingSenderSettings}
                className="w-full"
              >
                {savingSenderSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Sender Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Email Logo & BIMI Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="logo-upload">Upload BIMI Logo (SVG only)</Label>
                      <Input
                        id="logo-upload"
                        type="file"
                        accept=".svg"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload an SVG logo for email branding (BIMI requirement)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="bimi-logo-url">BIMI Logo URL</Label>
                      <Input
                        id="bimi-logo-url"
                        placeholder="https://your-domain.com/logo.svg"
                        value={brandingSettings.bimiLogoUrl}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, bimiLogoUrl: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="bimi-selector">BIMI Selector</Label>
                      <Input
                        id="bimi-selector"
                        placeholder="default"
                        value={brandingSettings.bimiSelector}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, bimiSelector: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email-domain">Email Domain</Label>
                      <Input
                        id="email-domain"
                        placeholder="yourdomain.com"
                        value={brandingSettings.emailDomain}
                        onChange={(e) => setBrandingSettings({ ...brandingSettings, emailDomain: e.target.value })}
                      />
                    </div>

                    <Button 
                      onClick={saveBrandingSettings} 
                      disabled={savingBrandingSettings}
                      className="w-full"
                    >
                      {savingBrandingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Branding Settings
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label>Generated BIMI TXT Record</Label>
                      <div className="flex gap-2">
                        <Input
                          value={generateBimiTxt()}
                          readOnly
                          className="font-mono text-sm"
                        />
                        <Button
                          onClick={copyBimiTxt}
                          variant="outline"
                          size="sm"
                          disabled={!brandingSettings.bimiLogoUrl || !brandingSettings.emailDomain}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Add this as a TXT record: {brandingSettings.bimiSelector}._bimi.{brandingSettings.emailDomain}
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="branding-test-email">Test Email Address</Label>
                      <div className="flex gap-2">
                        <Input
                          id="branding-test-email"
                          type="email"
                          placeholder="test@example.com"
                          value={brandingTestEmail}
                          onChange={(e) => setBrandingTestEmail(e.target.value)}
                        />
                        <Button
                          onClick={sendBrandingTestEmail}
                          disabled={sendingBrandingTest}
                          variant="outline"
                        >
                          {sendingBrandingTest && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Send a test email to verify branding display
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  DMARC Setup Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-800">DMARC Required for BIMI</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        BIMI requires proper DMARC configuration to display logos in email clients.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">Required DNS Records:</h4>
                  
                  <div>
                    <Label>1. DMARC TXT Record</Label>
                    <div className="bg-muted p-3 rounded border font-mono text-sm text-foreground break-words">
                      <p><strong>Name:</strong> _dmarc.{brandingSettings.emailDomain || 'yourdomain.com'}</p>
                      <p><strong>Value:</strong> v=DMARC1; p=quarantine; rua=mailto:dmarc@{brandingSettings.emailDomain || 'yourdomain.com'}</p>
                    </div>
                  </div>

                  <div>
                    <Label>2. SPF TXT Record</Label>
                    <div className="bg-muted p-3 rounded border font-mono text-sm text-foreground break-words">
                      <p><strong>Name:</strong> {brandingSettings.emailDomain || 'yourdomain.com'}</p>
                      <p><strong>Value:</strong> v=spf1 include:_spf.google.com include:spf.resend.com ~all</p>
                    </div>
                  </div>

                  <div>
                    <Label>3. DKIM Setup</Label>
                    <div className="bg-muted p-3 rounded border text-sm text-foreground">
                      <p>Configure DKIM through your email service provider (Resend, Gmail, etc.)</p>
                      <p>Each provider will give you specific DKIM records to add to your DNS.</p>
                    </div>
                  </div>

                  <div>
                    <Label>4. BIMI TXT Record (Generated Above)</Label>
                    <div className="bg-muted p-3 rounded border font-mono text-sm text-foreground break-words">
                      <p><strong>Name:</strong> {brandingSettings.bimiSelector}._bimi.{brandingSettings.emailDomain || 'yourdomain.com'}</p>
                      <p><strong>Value:</strong> {generateBimiTxt()}</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Important Notes:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• BIMI support varies by email client (Gmail, Yahoo, Apple Mail)</li>
                      <li>• Logo display may take 24-48 hours after DNS propagation</li>
                      <li>• Some email providers require VMC (Verified Mark Certificate) for wider support</li>
                      <li>• Test your setup with multiple email clients</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
        </>
      )}
    </div>
  );
};

export default EmailNotificationsPage;